import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMyOrg } from "@/hooks/use-my-org";
import { listClubCoachProfiles, type ClubCoachProfileDraft } from "@/libs/staffApi";
import {
  attestTeaching,
  correctTeachingEvidence,
  listTeachingEvidence,
  listTeachingEvidenceHistory,
  revokeTeachingEvidence,
  type ProgramTeachingEvidenceResponse,
  type UnifiedProgramResponse,
  type UnifiedProgramSessionResponse,
} from "@/libs/programsApi";

type Outcome = "completed" | "partial" | "coach_no_show" | "cancelled";

function utcInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const value = new Date(iso);
  return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 16);
}

function asUtc(value: string): string {
  return new Date(`${value}:00Z`).toISOString();
}

export function TeachingEvidenceDialog({ program, session, open, onClose }: {
  program: UnifiedProgramResponse;
  session: UnifiedProgramSessionResponse | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const orgView = useMyOrg();
  const orgId = orgView.org?.org_id;
  const [outcome, setOutcome] = useState<Outcome>("completed");
  const [actualStart, setActualStart] = useState("");
  const [actualEnd, setActualEnd] = useState("");
  const [selectedProfiles, setSelectedProfiles] = useState<ClubCoachProfileDraft[]>([]);
  const [reason, setReason] = useState("");
  const [historyBefore, setHistoryBefore] = useState<number | undefined>();

  const evidenceQuery = useQuery({
    queryKey: ["programs", program.program_id, "teaching-evidence"],
    queryFn: () => listTeachingEvidence(program.program_id, {
      trackId: session?.track_id ?? undefined,
      occurrenceStart: session?.occurrence_start ?? session?.start_time,
    }),
    enabled: open && Boolean(session?.track_id),
    retry: false,
  });
  const profilesQuery = useQuery({
    queryKey: ["staff", orgId, "club-coach-profiles"],
    queryFn: () => listClubCoachProfiles(orgId ?? ""),
    enabled: open && Boolean(orgId),
    retry: false,
  });
  const existing = useMemo(() => evidenceQuery.data?.find((item) =>
    item.track_id === session?.track_id &&
    item.occurrence_start === (session?.occurrence_start ?? session?.start_time)),
  [evidenceQuery.data, session]);
  const profileOptions = useMemo(() => (profilesQuery.data ?? []).filter((profile) =>
    profile.status === "published" && (profile.facilities ?? []).some((binding) =>
      binding.facility_id === program.facility_id && binding.status === "active")),
  [profilesQuery.data, program.facility_id]);
  const historyQuery = useQuery({
    queryKey: ["programs", program.program_id, "teaching-evidence", existing?.evidence_id, "history", historyBefore],
    queryFn: () => listTeachingEvidenceHistory(program.program_id, existing!.evidence_id, historyBefore),
    enabled: open && Boolean(existing),
    retry: false,
  });

  useEffect(() => {
    if (!open || !session) return;
    setOutcome(existing?.outcome ?? "completed");
    setActualStart(utcInput(existing?.actual_start ?? session.start_time));
    setActualEnd(utcInput(existing?.actual_end ?? session.end_time));
    setSelectedProfiles(profileOptions.filter((profile) =>
      existing?.contributors.some((item) => item.coach_profile_id === profile.coach_profile_id)
      || (!existing && profile.coach_profile_id === program.coach_profile_id)));
    setReason("");
    setHistoryBefore(undefined);
  }, [existing, open, profileOptions, program.coach_profile_id, session]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!session?.track_id) throw new Error("This session has no track.");
      const taught = outcome === "completed" || outcome === "partial";
      const start = taught ? asUtc(actualStart) : null;
      const end = taught ? asUtc(actualEnd) : null;
      const contributors = taught ? selectedProfiles.map((profile, index) => ({
        coach_profile_id: profile.coach_profile_id,
        role: index === 0 ? "head" as const : "assistant" as const,
        attributable_start: start as string,
        attributable_end: end as string,
      })) : [];
      if (existing) {
        return correctTeachingEvidence(program.program_id, existing.evidence_id, {
          outcome, actual_start: start, actual_end: end, contributors,
          correction_reason: reason.trim(), expected_revision: existing.revision,
          idempotency_key: crypto.randomUUID(),
        });
      }
      return attestTeaching(program.program_id, {
        track_id: session.track_id,
        occurrence_start: session.occurrence_start ?? session.start_time,
        outcome, actual_start: start, actual_end: end, contributors,
        correction_reason: null, expected_revision: 0,
        idempotency_key: crypto.randomUUID(),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["programs", program.program_id, "teaching-evidence"],
      });
      onClose();
    },
  });
  const revokeMutation = useMutation({
    mutationFn: () => revokeTeachingEvidence(
      program.program_id, existing!.evidence_id,
      { reason: reason.trim(), expected_revision: existing!.revision,
        idempotency_key: crypto.randomUUID() },
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["programs", program.program_id, "teaching-evidence"],
      });
      onClose();
    },
  });
  const taught = outcome === "completed" || outcome === "partial";
  const canSubmit = Boolean(session?.track_id) && (!taught || (
    actualStart && actualEnd && selectedProfiles.length > 0
  )) && (!existing || reason.trim().length > 0);

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{existing ? "Correct teaching record" : "Review teaching"}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="info">This creates club-attested experience only. It does not charge, refund, consume credits, or change bookings.</Alert>
        {evidenceQuery.isError || profilesQuery.isError ? <Alert severity="error">Teaching details could not be loaded.</Alert> : null}
        <TextField select label="Outcome" value={outcome} onChange={(event) => setOutcome(event.target.value as Outcome)}>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="partial">Partially completed</MenuItem>
          <MenuItem value="coach_no_show">Coach no-show</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        {taught ? <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField fullWidth label="Actual start (UTC)" type="datetime-local" value={actualStart} onChange={(event) => setActualStart(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField fullWidth label="Actual end (UTC)" type="datetime-local" value={actualEnd} onChange={(event) => setActualEnd(event.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          </Stack>
          <Autocomplete multiple options={profileOptions} value={selectedProfiles}
            getOptionLabel={(option) => option.display_name}
            isOptionEqualToValue={(left, right) => left.coach_profile_id === right.coach_profile_id}
            onChange={(_event, value) => setSelectedProfiles(value)}
            renderInput={(params) => <TextField {...params} label="Coaches who taught" helperText="The first coach is recorded as lead; additional coaches are co-instructors." />} />
        </> : null}
        {existing ? <TextField label="Correction or revocation reason" required multiline minRows={2} value={reason} onChange={(event) => setReason(event.target.value)} /> : null}
        {existing ? <Typography variant="caption" color="text.secondary">Current record: revision {existing.revision} · {existing.qualifying_student_count} qualifying learner{existing.qualifying_student_count === 1 ? "" : "s"} · Club-attested</Typography> : null}
        {historyQuery.data && historyQuery.data.items.length > 1 ? <Stack spacing={0.5} aria-label="Correction history">
          <Typography variant="subtitle2">Correction history</Typography>
          {historyQuery.data.items.map((item) => <Typography key={item.revision} variant="caption" color="text.secondary">
            Revision {item.revision}: {item.outcome.replaceAll("_", " ")} · {item.qualifying_student_count} qualifying learner{item.qualifying_student_count === 1 ? "" : "s"}{item.correction_reason ? ` · ${item.correction_reason}` : ""}
          </Typography>)}
          {historyQuery.data.has_more && historyQuery.data.next_before_revision ? <Button size="small" sx={{ alignSelf: "flex-start" }}
            onClick={() => setHistoryBefore(historyQuery.data?.next_before_revision ?? undefined)}>View older revisions</Button> : null}
        </Stack> : null}
        {mutation.isError ? <Alert severity="error">{mutation.error instanceof Error ? mutation.error.message : "Teaching record could not be saved."}</Alert> : null}
        {revokeMutation.isError ? <Alert severity="error">{revokeMutation.error instanceof Error ? revokeMutation.error.message : "Teaching record could not be revoked."}</Alert> : null}
      </Stack>
    </DialogContent>
    <DialogActions>
      {existing ? <Button color="error" disabled={!reason.trim() || revokeMutation.isPending || mutation.isPending}
        onClick={() => revokeMutation.mutate()}>Revoke record</Button> : null}
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}>{mutation.isPending ? "Saving…" : existing ? "Save correction" : "Save teaching record"}</Button>
    </DialogActions>
  </Dialog>;
}
